import { get } from 'lodash-es';
import { useState, Fragment } from 'react';
import { useMutation } from '@apollo/client/react';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { makeStyles } from 'tss-react/mui';
import type { GroupPost } from '../../types/GroupPost';
import { useAppContext } from '../../contexts/app';
import {
  updateGroupPostMemberNotificationPreferenceMutation,
  groupPostQuery
} from '../../apollo/groups';

const useStyles = makeStyles()((theme) => ({
  button: {
    padding: theme.spacing(0, 1)
  }
}));

type Props = {
  groupPost?: GroupPost;
};

const GroupPostMemberNotificationPreferenceToggle = ({ groupPost }: Props) => {
  const { isLoggedIn } = useAppContext();
  const [updateGroupPostMemberNotificationPreference] = useMutation(updateGroupPostMemberNotificationPreferenceMutation, {
    update: (proxy, { data: { updateGroupPostMemberNotificationPreference: membership } }) => {
      const queryVariables = { id: groupPost.id };
      const data = proxy.readQuery({
        query: groupPostQuery,
        variables: queryVariables
      });
      proxy.writeQuery({
        query: groupPostQuery,
        variables: queryVariables,
        data: {
          ...data,
          groupPost: {
            ...data.groupPost,
            membership
          }
        }
      });
    }
  });
  const [submitting, setSubmitting] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const { classes } = useStyles();

  if (!isLoggedIn || !groupPost.group.membership) {
    return null;
  }

  // show subscribed if subscribed to group or message but NOT if unsubscribed from group
  const subscribed =
    get(groupPost.group.membership, 'messageNotificationPreference') !== 'NONE' &&
    ( groupPost.membership ?
        get(groupPost.membership, 'notificationPreference') === 'ALL' :
        get(groupPost.group.membership, 'messageNotificationPreference') === 'ALL'
    );
  // disable if unsubscribed from comments on group level
  const disabled = submitting ||
    get(groupPost.group.membership, 'messageNotificationPreference') === 'NONE';
  return (
    <Fragment>
      <Button
        onClick={(e) => {
          setAnchorEl(e.currentTarget);
        }}
        disabled={disabled}
        aria-controls="group-message-member-notification-preference-menu"
        aria-haspopup="true"
        color="primary"
        // variant="outlined"
        className={classes.button}
      >
        {subscribed ? 'Subscribed' : 'Unsubscribed'}
      </Button>
      <Menu
        id="group-message-member-notification-preference-menu"
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={() => {
          setAnchorEl(null);
        }}
      >
        <MenuItem
          onClick={() => {
            setAnchorEl(null);
            setSubmitting(true);
            updateGroupPostMemberNotificationPreference({
              variables: {
                groupPostId: groupPost.id,
                preference: subscribed ? 'NONE' : 'ALL'
              }
            }).then(() => setSubmitting(false));
          }}
        >
          {subscribed ?
            'Unsubscribe from comments' :
            'Subscribe to comments'}
        </MenuItem>
      </Menu>
    </Fragment>
  );
};

export default GroupPostMemberNotificationPreferenceToggle;
